import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, ThumbsDown, MessageSquare, Send, CheckCircle, XCircle, Clock, Filter } from 'lucide-react';
import { getPendingReviews, getAllReviews, approveReview, rejectReview, respondToReview, getReviewStats } from '../utils/api';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [filterStatus]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = filterStatus === 'pending' 
        ? await getPendingReviews()
        : await getAllReviews();
      
      const filtered = filterStatus === 'all' 
        ? data 
        : data.filter(r => r.status === filterStatus);
      
      setReviews(filtered);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getReviewStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await approveReview(reviewId);
      await fetchReviews();
      await fetchStats();
    } catch (error) {
      console.error('Error approving review:', error);
      alert('Failed to approve review');
    }
  };

  const handleReject = async (reviewId) => {
    try {
      await rejectReview(reviewId);
      await fetchReviews();
    } catch (error) {
      console.error('Error rejecting review:', error);
      alert('Failed to reject review');
    }
  };

  const handleRespond = async (reviewId) => {
    if (!adminResponse.trim()) {
      alert('Please enter a response');
      return;
    }

    try {
      setSubmitting(true);
      await respondToReview(reviewId, adminResponse);
      setRespondingTo(null);
      setAdminResponse('');
      await fetchReviews();
    } catch (error) {
      console.error('Error responding to review:', error);
      alert('Failed to send response');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold border border-green-300 flex items-center space-x-1">
          <CheckCircle className="w-3 h-3" />
          <span>Approved</span>
        </span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold border border-red-300 flex items-center space-x-1">
          <XCircle className="w-3 h-3" />
          <span>Rejected</span>
        </span>;
      default:
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold border border-yellow-300 flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Pending</span>
        </span>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Review Management</h1>
          <p className="text-gray-400">Moderate and respond to customer reviews</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="text-3xl font-bold text-cyan-400 mb-2">{stats.totalReviews}</div>
              <div className="text-gray-400 text-sm">Total Reviews</div>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="text-3xl font-bold text-yellow-400 mb-2 flex items-center">
                <Star className="w-8 h-8 fill-yellow-400 mr-2" />
                {stats.averageRating}
              </div>
              <div className="text-gray-400 text-sm">Average Rating</div>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="text-3xl font-bold text-green-400 mb-2">{stats.ratingDistribution['5']}</div>
              <div className="text-gray-400 text-sm">5-Star Reviews</div>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="text-3xl font-bold text-orange-400 mb-2">
                {reviews.filter(r => r.status === 'pending').length}
              </div>
              <div className="text-gray-400 text-sm">Pending Approval</div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-gray-800 p-4 rounded-xl mb-6 border border-gray-700">
          <div className="flex items-center space-x-2 mb-3">
            <Filter className="w-5 h-5 text-cyan-400" />
            <span className="text-white font-semibold">Filter Reviews:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'pending'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilterStatus('approved')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'approved'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilterStatus('rejected')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'rejected'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Rejected
            </button>
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterStatus === 'all'
                  ? 'bg-gray-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              All
            </button>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="bg-gray-800 p-12 rounded-xl text-center border border-gray-700">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-gray-800 p-12 rounded-xl text-center border border-gray-700">
            <p className="text-gray-400 text-lg">No reviews found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:border-cyan-500 transition-all">
                {/* Review Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-white font-semibold">{review.customerName}</h3>
                      {getStatusBadge(review.status)}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-400">
                      <span>Booking: #{review.bookingId}</span>
                      <span>•</span>
                      <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  {/* Star Rating */}
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Review Comment */}
                <div className="bg-gray-900 p-4 rounded-lg mb-4 border border-gray-700">
                  <p className="text-gray-300 leading-relaxed">{review.comment}</p>
                </div>

                {/* Admin Response (if exists) */}
                {review.adminResponse && (
                  <div className="bg-cyan-900/20 p-4 rounded-lg mb-4 border border-cyan-700">
                    <div className="flex items-center space-x-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400 font-semibold text-sm">Your Response:</span>
                    </div>
                    <p className="text-gray-300 text-sm">{review.adminResponse}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  {review.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(review.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 font-medium"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleReject(review.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 font-medium"
                      >
                        <ThumbsDown className="w-4 h-4" />
                        <span>Reject</span>
                      </button>
                    </>
                  )}
                  
                  {(review.status === 'approved' && !review.adminResponse) && (
                    <button
                      onClick={() => setRespondingTo(review.id)}
                      className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors flex items-center space-x-2 font-medium"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>Respond</span>
                    </button>
                  )}
                </div>

                {/* Response Form */}
                {respondingTo === review.id && (
                  <div className="mt-4 bg-gray-900 p-4 rounded-lg border border-cyan-700">
                    <label className="block text-white font-medium mb-2">Your Response:</label>
                    <textarea
                      value={adminResponse}
                      onChange={(e) => setAdminResponse(e.target.value)}
                      rows="3"
                      placeholder="Thank you for your feedback..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 mb-3"
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRespond(review.id)}
                        disabled={submitting || !adminResponse.trim()}
                        className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 font-medium"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            <span>Send Response</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setRespondingTo(null);
                          setAdminResponse('');
                        }}
                        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsManagement;
